import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  adjustCollectionAllocation,
  createCollectionAllocations,
} from '#/api/crmMgmt/customerOrder/collection';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  allocationEffectiveAmount,
  sumAmounts,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import useCollectionMutation from './useCollectionMutation';

function salesOrderOptions(salesOrders = [], excludeId = null) {
  return salesOrders
    .filter((so) => so.status !== 'CANCELLED' && so.id !== excludeId)
    .map((so) => ({
      value: so.id,
      label: `${so.salesOrderNo ?? so.id}（${so.status ?? ''}・應收 ${formatDisplayAmount(so.totalAmount)}）`,
    }));
}

/** 分配到銷貨單：一次一筆（同 API 可多筆）。開票前分配只記用途，開票且足額才 PAID。 */
export const CollectionAllocationCreateDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  receipts,
  salesOrders,
}) => {
  const [receiptId, setReceiptId] = useState('');
  const [salesOrderId, setSalesOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState(null);

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => createCollectionAllocations(customerOrderId, payload, { idempotencyKey }),
    { successMessage: '已分配到銷貨單', onSuccess: () => onOpenChange(false) },
  );

  const receiptOptions = receipts
    .filter((receipt) => receipt.status === 'CONFIRMED')
    .map((receipt) => ({
      value: receipt.id,
      label: `${formatDateTimeOrDash(receipt.actualReceivedAt)}・${formatDisplayAmount(receipt.amount)}`,
    }));

  function handleConfirm() {
    let error = null;
    if (!receiptId || !salesOrderId) {
      error = '請選擇實收與銷貨單';
    } else if (!(Number(amount) > 0)) {
      error = '分配金額須大於 0';
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({ allocations: [{ receiptId, salesOrderId, amount: Number(amount) }] });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="分配到銷貨單"
      description="同一筆實收的子單分配合計不可超過其可用額，單張子單不可超過應收額。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '分配中…' : '分配'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <SelectCore
          id="allocation-receipt"
          label="來源實收"
          options={receiptOptions}
          value={receiptId}
          onChange={setReceiptId}
        />
        <SelectCore
          id="allocation-sales-order"
          label="銷貨單"
          options={salesOrderOptions(salesOrders)}
          value={salesOrderId}
          onChange={setSalesOrderId}
        />
        <FloatingInput
          id="allocation-amount"
          type="number"
          label="分配金額"
          min={0}
          step="0.01"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionAllocationCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  receipts: PropTypes.arrayOf(PropTypes.object).isRequired,
  salesOrders: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * 移轉分配：同一來源分配從原子單移到另一張子單。
 * expected 值＝目前有效額；餘額已變動時後端回 409，重新讀取後再決定。
 */
export const CollectionAllocationAdjustDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  allocation,
  allocations,
  salesOrders,
}) => {
  const [toSalesOrderId, setToSalesOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState(null);
  const fromAmount = allocationEffectiveAmount(allocation);

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => adjustCollectionAllocation(customerOrderId, payload, { idempotencyKey }),
    { successMessage: '已移轉分配', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    let error = null;
    if (!toSalesOrderId) {
      error = '請選擇目標銷貨單';
    } else if (!(Number(amount) > 0) || Number(amount) > fromAmount) {
      error = `移轉金額須大於 0 且不超過目前有效額 ${formatDisplayAmount(fromAmount)}`;
    } else if (!reason.trim()) {
      error = '請填寫原因';
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    const expectedToAmount = sumAmounts(
      allocations
        .filter((row) => row.receiptId === allocation.receiptId && row.salesOrderId === toSalesOrderId)
        .map(allocationEffectiveAmount),
    );
    mutation.mutate({
      sourceAllocationId: allocation.id,
      fromSalesOrderId: allocation.salesOrderId,
      toSalesOrderId,
      amount: Number(amount),
      reason: reason.trim(),
      expectedFromAmount: fromAmount,
      expectedToAmount,
    });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="移轉分配"
      description={`目前有效額 ${formatDisplayAmount(fromAmount)}；不修改原收款紀錄，會留下調整紀錄。`}
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '移轉中…' : '移轉'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <SelectCore
          id="allocation-adjust-to"
          label="目標銷貨單"
          options={salesOrderOptions(salesOrders, allocation.salesOrderId)}
          value={toSalesOrderId}
          onChange={setToSalesOrderId}
        />
        <FloatingInput
          id="allocation-adjust-amount"
          type="number"
          label="移轉金額"
          min={0}
          step="0.01"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <FloatingTextarea
          id="allocation-adjust-reason"
          label="原因"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionAllocationAdjustDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  allocation: PropTypes.object.isRequired,
  allocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  salesOrders: PropTypes.arrayOf(PropTypes.object).isRequired,
};
